import React from 'react';
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { X, AlertTriangle, CircleAlert } from 'lucide-react';
import Button from '../../../Shared/Buttons/Button';

export default function DeleteConfirmDialog({ open, onClose, onConfirm, title, item, consequences, loading, error }) {
    const classes = useStyles();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: classes.dialogPaper }}
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title}>
                    {title}
                </DialogTitle>
                <button onClick={onClose} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__content}>
                <div className={classes.warningBox}>
                    <AlertTriangle size={20} className={classes.warningIcon} />
                    <div>
                        <p className={classes.warningText}>
                            You are about to delete <strong>{item}</strong>.
                        </p>
                        {consequences && consequences.length > 0 && (
                            <>
                                <p className={classes.consequenceLabel}>This will:</p>
                                <ul className={classes.consequenceList}>
                                    {consequences.map((c, i) => (
                                        <li key={i}>{c}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <p className={classes.noteText}>
                            Accounts that are currently used by price bundles or project details cannot be deleted.
                        </p>
                    </div>
                </div>
                {error && (
                    <div className={classes.errorBox}>
                        <CircleAlert size={18} className={classes.errorIcon} />
                        <p className={classes.errorText}>{error}</p>
                    </div>
                )}
            </DialogContent>
            <div className={classes.dialog__actions}>
                <Button variant="outlined" onClick={onClose}>{error ? 'Close' : 'Cancel'}</Button>
                {!error && <Button variant="destructive" onClick={onConfirm} loading={loading}>Delete</Button>}
            </div>
        </Dialog>
    );
}

const useStyles = makeStyles(() => ({
    dialogPaper: {
        width: '460px !important',
        borderRadius: '6px !important',
        padding: '16px !important',
    },
    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',

    },
    dialog__title: {
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        fontSize: '20px',
        color: '#000',
        padding: '0 !important',
        '& h2': {
            fontWeight: 'normal',
            fontSize: '20px !important',
        },
    },
    dialog__closeButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
    },
    dialog__content: {
        padding: '0 !important',
        marginTop: '12px',
    },
    warningBox: {
        display: 'flex',
        gap: '12px',
        backgroundColor: '#fff8f0',
        border: '1px solid #ffe0b2',
        borderRadius: '6px',
        padding: '14px',
    },
    warningIcon: {
        color: '#e65100',
        flexShrink: 0,
        marginTop: '2px',
    },
    warningText: {
        margin: 0,
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        color: '#333',
    },
    consequenceLabel: {
        margin: '10px 0 4px 0',
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#333',
    },
    consequenceList: {
        margin: '0 0 0 16px',
        padding: 0,
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        color: '#333',
        '& li': {
            marginBottom: '4px',
        },
    },
    noteText: {
        margin: '10px 0 0 0',
        fontFamily: 'Lato, sans-serif',
        fontSize: '12px',
        color: '#888',
        fontStyle: 'italic',
    },
    errorBox: {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        padding: '14px',
        marginTop: '12px',
    },
    errorIcon: {
        color: '#dc2626',
        flexShrink: 0,
        marginTop: '2px',
    },
    errorText: {
        margin: 0,
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        color: '#991b1b',
    },
    dialog__actions: {
        padding: '0 !important',
        marginTop: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
    },
}));
